import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CartContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('naturewayz_session');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('naturewayz_session', sessionId);
  }
  return sessionId;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const sessionId = getSessionId();

  // Fetch cart on mount
  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, [sessionId]);

  // Fetch all products for cart item details
  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  useEffect(() => {
    fetchCart();
    fetchProducts();
  }, [fetchCart, fetchProducts]);

  // Add to cart
  const addToCart = async (productId, quantity = 1) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/cart/${sessionId}/add`, {
        product_id: productId,
        quantity
      });
      setCart(response.data.cart);
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update cart item quantity
  const updateQuantity = async (productId, quantity) => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/cart/${sessionId}/update`, {
        product_id: productId,
        quantity
      });
      setCart(response.data.cart);
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Remove from cart
  const removeFromCart = async (productId) => {
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/cart/${sessionId}/remove/${productId}`);
      setCart(response.data.cart);
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Clear cart
  const clearCart = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/cart/${sessionId}/clear`);
      setCart({ items: [] });
    } catch (error) {
      console.error('Error clearing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get cart items with product details
  const getCartItemsWithDetails = () => {
    if (!cart.items || !products.length) return [];
    
    return cart.items.map(item => {
      const product = products.find(p => p.id === item.product_id);
      return {
        ...item,
        product
      };
    }).filter(item => item.product);
  };

  // Calculate totals
  const getCartTotals = () => {
    const items = getCartItemsWithDetails();
    const subtotal = items.reduce((sum, item) => {
      return sum + (item.product.price * item.quantity);
    }, 0);
    const shipping = subtotal > 0 ? 200 : 0;
    const total = subtotal + shipping;
    
    return { subtotal, shipping, total };
  };

  // Get cart count
  const cartCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      sessionId,
      cartCount,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      getCartItemsWithDetails,
      getCartTotals,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
